import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import { useEffect, useState } from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';
import { handleError, handleSuccess } from "../../../Utils";
import { api } from "../../../ApiServices/ApiServices";

function ReferralRequest() {
  const navigate = useNavigate();
  const [selectedAmount, setSelectedAmount] = useState(null);
  const [customAmount, setCustomAmount] = useState("");
  const [platformFee, setPlatformFee] = useState(10);
  const [errors, setErrors] = useState({}); // Add errors state
  const [loading, setLoading] = useState(false);
  
  // Form state
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phoneNumber: "",
    question: "",
    resume: null,
    jobLink: ""
  });

  useEffect(() => {
    document.title = "CodeQueue - Booking preview";
  });

  const location = useLocation();
  const state = location.state;
  
  if (!state) {
    return <Navigate to="/mentors-list" />;
  }
  
  const {
    mentorId,
    selectedPriceIndex,
    mentorName,
    sessionType,
    profileImage,
  } = location.state || {};

  const totalAmount =
    (parseInt(selectedAmount) || parseInt(customAmount) || 0) +
    platformFee +
    sessionType[selectedPriceIndex]?.amount;

  // Form validation function
  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = "Name is required";
    }

    if (!formData.email.trim()) {
      newErrors.email = "Email is required";
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = "Please enter a valid email address";
    }

    if (!formData.phoneNumber.trim()) {
      newErrors.phoneNumber = "Phone number is required";
    } else if (formData.phoneNumber.length < 10) {
      newErrors.phoneNumber = "Please enter a valid phone number";
    }

    if (!formData.question.trim()) {
      newErrors.question = "Question/Details are required";
    } else if (formData.question.trim().length < 10) {
      newErrors.question = "Please provide more detailed information (at least 10 characters)";
    }

    if (!formData.resume) {
      newErrors.resume = "Resume upload is required";
    }

    if (!formData.jobLink.trim()) {
      newErrors.jobLink = "Job ID/Job Link is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ""
      }));
    }
  };

  // Handle phone number change
  const handlePhoneChange = (value) => {
    setFormData(prev => ({
      ...prev,
      phoneNumber: value
    }));

    if (errors.phoneNumber) {
      setErrors(prev => ({
        ...prev,
        phoneNumber: ""
      }));
    }
  };

  // Handle file upload
  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) { // 5MB in bytes
        setErrors(prev => ({
          ...prev,
          resume: "Resume file size should be less than 5MB",
        }));
        setFormData(prev => ({
          ...prev,
          resume: null,
        }));
        return;
      }
      setFormData(prev => ({
        ...prev,
        resume: file
      }));
      if (errors.resume) {
        setErrors(prev => ({
          ...prev,
          resume: ""
        }));
      }
    }
  };

  // Handle amount selection
  const handleAmountClick = (amount) => {
    setSelectedAmount(amount);
    setCustomAmount("");
  };

  // Handle custom amount input
  const handleCustomAmountChange = (e) => {
    setCustomAmount(e.target.value);
    setSelectedAmount(null);
  };

  // Check if form is valid
  const isFormValid = () => {
    return (
      formData.name.trim() &&
      formData.email.trim() &&
      /\S+@\S+\.\S+/.test(formData.email) &&
      formData.phoneNumber.trim() &&
      formData.phoneNumber.length >= 10 &&
      formData.question.trim() &&
      formData.question.trim().length >= 10 &&
      formData.resume &&
      formData.jobLink.trim()
    );
  };

  // Confirm payment with validation
  const handleCreateOrder = async () => {
    if (!validateForm()) {
      handleError("Please fill in all required fields correctly");
      return;
    }

    try {
      const response = await api.post("/create-order", { amount: totalAmount });
      handleVerifyOrder(response.data.data);
    } catch (error) {
      handleError("Failed to create order. Please try again.");
    }
  };

   const handleVerifyOrder = async (data) => {
    const razorpayKeyId = "rzp_live_aiGKQHLKnNOrz8";
    const options = {
      key: razorpayKeyId,
      amount: data.amount,
      currency: data.currency,
      name: "CodeQueue",
      description: "CodeQueue",
      order_id: data.id,
      handler: async (response) => {
        try {
          setLoading(true);
          const formDataToSend = new FormData();
          formDataToSend.append("razorpay_order_id", response.razorpay_order_id);
          formDataToSend.append("razorpay_payment_id", response.razorpay_payment_id);
          formDataToSend.append("razorpay_signature", response.razorpay_signature);
          formDataToSend.append("session_title", sessionType[selectedPriceIndex]?.title);
          formDataToSend.append("mentorId", mentorId);
          formDataToSend.append("user_name", formData.name);
          formDataToSend.append("user_email", formData.email);
          formDataToSend.append("user_phone", formData.phoneNumber);
          formDataToSend.append("question", formData.question);
          formDataToSend.append("resume", formData.resume); // ✅ FILE!
          formDataToSend.append("jobLink", formData.jobLink);

          // Object fields must be stringified before sending
          formDataToSend.append("amount", JSON.stringify({
            service_amount: parseInt(sessionType[selectedPriceIndex]?.amount),
            platform_fee: parseInt(platformFee),
            additional_amount: parseInt(selectedAmount) || parseInt(customAmount) || 0,
          }));

          const res = await api.post("/verify-order-referral-request", formDataToSend, {
            headers: {
              "Content-Type": "multipart/form-data",
            },
          });

          const verifyData = res?.data;
          if (verifyData?.message) {
            handleSuccess(verifyData?.message);
          }
          navigate("/");
        } catch (error) {
          return handleError(error?.response?.data?.message);
        }finally{
          setLoading(false);
        }
      },
      theme: {
        color: "#5f63b8",
      },
    };
    const rzp1 = new window.Razorpay(options);
    rzp1.open();
  };

  return (
    <div className="min-h-screen flex flex-col bg-blue-900 py-5">
      <div className="flex-1 overflow-y-auto pb-20">
        <div className="bg-white rounded-lg shadow-lg w-full max-w-lg mx-auto p-6">
          {/* Header */}
          <div className="flex items-center space-x-4">
            <ArrowBackIcon
              className="text-black cursor-pointer"
              onClick={() => navigate(-1)}
            />
            <img
              src={profileImage}
              alt="User Profile"
              className="rounded-full w-10 h-10"
            />
            <h1 className="font-medium text-lg">{mentorName}</h1>
          </div>

          {/* Webinar Info */}
          <div className="mt-6">
            <h2 className="text-xl font-bold">
              {sessionType[selectedPriceIndex]?.title}
            </h2>
            <p className="text-gray-600">{sessionType[selectedPriceIndex]?.type}</p>
          </div>

          {/* Form Section */}
          <div className="mt-6">
            <div className="bg-gray-50 p-4 rounded-lg mb-6">
              <p className="text-sm text-gray-700 mb-4">
                <strong>Do not ask for referral at multiple companies in one message!</strong>
              </p>
              <p className="text-sm text-gray-700 mb-2">
                Please share following details in same sequence:
              </p>
              <ul className="text-sm text-gray-700 mb-4 space-y-1">
                <li>A) Full Name</li>
                <li>B) Passing Degree and Year of Graduation</li>
                <li>C) Degree e.g ( Btech, MBA, BBA etc)</li>
                <li>D) Current Company name and designation and Years of Work experience</li>
                <li>E) Upload resume to GDrive and share link with this message</li>
                <li>F) ATS Score ( Check for free on jobscan.co )</li>
                <li>G) Target Company and Role Name and mention Job Id or JD Url (Meesho, Morgan Stanley, JP Morgan, Tiger Analytics, Fractal Analytics)</li>
                <li>H) Are you getting interview calls for similar job roles?</li>
              </ul>
              <p className="text-sm text-gray-700 mb-4">
                <strong>Referrals are provided only to candidates where referer feels higher chances of interview conversions.</strong>
              </p>
              <p className="text-sm text-gray-700 mb-4">
                <strong>Your Request will be cancelled if:</strong>
              </p>
              <ul className="text-sm text-gray-700 mb-4 space-y-1">
                <li>&gt;&gt; You are randomly applying to job role and your profile does not match details mentioned in Job Description</li>
                <li>&gt;&gt; Referral slots are finished</li>
                <li>&gt;&gt; You have provided in-accurate or incomplete details as asked in above message</li>
              </ul>
              <p className="text-sm text-gray-700">
                <strong>Best of Luck!!</strong>
              </p>
            </div>

            {/* Form Fields */}
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="name"
                  placeholder="Enter your name"
                  value={formData.name}
                  onChange={handleInputChange}
                  className={`w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 transition-colors ${
                    errors.name
                      ? "border-red-500 focus:ring-red-500"
                      : "border-gray-300 focus:ring-blue-500"
                  }`}
                />
                {errors.name && (
                  <p className="text-red-500 text-xs mt-1">{errors.name}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email <span className="text-red-500">*</span>
                </label>
                <input
                  type="email"
                  name="email"
                  placeholder="Enter your email"
                  value={formData.email}
                  onChange={handleInputChange}
                  className={`w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 transition-colors ${
                    errors.email
                      ? "border-red-500 focus:ring-red-500"
                      : "border-gray-300 focus:ring-blue-500"
                  }`}
                />
                {errors.email && (
                  <p className="text-red-500 text-xs mt-1">{errors.email}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Phone number <span className="text-red-500">*</span>
                </label>
                <PhoneInput
                  country={'in'}
                  value={formData.phoneNumber}
                  onChange={handlePhoneChange}
                  inputStyle={{
                    width: '100%',
                    height: '42px',
                    fontSize: '16px',
                    border: errors.phoneNumber ? '1px solid #ef4444' : '1px solid #d1d5db',
                    borderRadius: '8px',
                    paddingLeft: '48px',
                    outline: 'none',
                    fontFamily: 'inherit',
                  }}
                  containerStyle={{
                    width: '100%',
                  }}
                  buttonStyle={{
                    border: errors.phoneNumber ? '1px solid #ef4444' : '1px solid #d1d5db',
                    borderRadius: '8px 0 0 8px',
                    backgroundColor: 'white',
                    outline: 'none',
                  }}
                  dropdownStyle={{
                    borderRadius: '8px',
                    border: '1px solid #d1d5db',
                  }}
                  inputProps={{
                    onFocus: (e) => {
                      if (!errors.phoneNumber) {
                        e.target.style.borderColor = '#3b82f6';
                        e.target.style.boxShadow = '0 0 0 2px rgba(59, 130, 246, 0.5)';
                      }
                    },
                    onBlur: (e) => {
                      e.target.style.borderColor = errors.phoneNumber ? '#ef4444' : '#d1d5db';
                      e.target.style.boxShadow = 'none';
                    }
                  }}
                />
                {errors.phoneNumber && (
                  <p className="text-red-500 text-xs mt-1">{errors.phoneNumber}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Your Question/Details <span className="text-red-500">*</span>
                </label>
                <textarea
                  name="question"
                  placeholder="Please provide all details as mentioned in the guidelines above (minimum 10 characters)"
                  value={formData.question}
                  onChange={handleInputChange}
                  rows={4}
                  className={`w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 resize-none transition-colors ${
                    errors.question
                      ? "border-red-500 focus:ring-red-500"
                      : "border-gray-300 focus:ring-blue-500"
                  }`}
                />
                <div className="flex justify-between items-center mt-1">
                  {errors.question && (
                    <p className="text-red-500 text-xs">{errors.question}</p>
                  )}
                  <p className="text-gray-400 text-xs ml-auto">
                    {formData.question.length}/10 minimum
                  </p>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Upload Your Resume <span className="text-red-500">*</span>
                </label>
                <div className={`border rounded-lg px-3 py-2 flex items-center justify-center bg-white h-[42px] focus-within:ring-2 transition-colors ${
                  errors.resume
                    ? "border-red-500 focus-within:ring-red-500"
                    : "border-gray-300 focus-within:ring-blue-500"
                }`}>
                  <input
                    type="file"
                    onChange={handleFileUpload}
                    accept=".pdf,.doc,.docx"
                    className="hidden"
                    id="resume-upload"
                  />
                  <label htmlFor="resume-upload" className="cursor-pointer flex items-center justify-center space-x-2 text-gray-700 w-full">
                    <div className="flex flex-col items-center">
                      <ArrowUpwardIcon className="text-gray-500 text-sm" />
                      <div className="w-4 h-0.5 bg-gray-500"></div>
                    </div>
                    <span className="text-sm text-center">
                      {formData.resume ? formData.resume.name : "Click to upload resume (PDF, DOC, DOCX)"}
                    </span>
                  </label>
                </div>
                {errors.resume && (
                  <p className="text-red-500 text-xs mt-1">{errors.resume}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Job ID/Job Link <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="jobLink"
                  placeholder="Enter job ID or job posting link"
                  value={formData.jobLink}
                  onChange={handleInputChange}
                  className={`w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 transition-colors ${
                    errors.jobLink
                      ? "border-red-500 focus:ring-red-500"
                      : "border-gray-300 focus:ring-blue-500"
                  }`}
                />
                {errors.jobLink && (
                  <p className="text-red-500 text-xs mt-1">{errors.jobLink}</p>
                )}
              </div>
            </div>
          </div>

          {/* Payment Options */}
          <div className="mt-6">
            <h3 className="text-lg font-bold mb-4">Pay what you like</h3>
            <div className="grid grid-cols-5 gap-2">
              {[100, 200, 300, 400, 500].map((amount) => (
                <button
                  key={amount}
                  className={`px-4 py-2 rounded-lg text-center transition-colors ${
                    selectedAmount === amount
                      ? "bg-green-600 text-white"
                      : "bg-gray-200 hover:bg-gray-300"
                  }`}
                  onClick={() => handleAmountClick(amount)}
                >
                  ₹{amount}
                </button>
              ))}
              <input
                type="number"
                placeholder="Other"
                value={customAmount}
                onChange={handleCustomAmountChange}
                className="border border-gray-300 rounded-lg px-4 py-2 col-span-5 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>

          {/* Order Summary */}
          <div className="mt-6">
            <h3 className="text-lg font-bold mb-4">Order Summary</h3>
            <div className="flex justify-between mb-2">
              <span>1 × {sessionType[selectedPriceIndex]?.title}</span>
              <span>₹{sessionType[selectedPriceIndex]?.amount}</span>
            </div>
            <div className="flex justify-between mb-2">
              <span>Platform fee</span>
              <span>₹{platformFee}</span>
            </div>
            <div className="flex justify-between mb-4">
              <span>Additional amount</span>
              <span>₹{selectedAmount || customAmount || 0}</span>
            </div>
            <div className="flex justify-between font-bold">
              <span>Total</span>
              <span>₹{totalAmount}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Fixed Footer */}
      <div className="fixed bottom-0 left-0 w-full bg-white shadow-lg">
        <div className="max-w-lg mx-auto p-4 flex justify-between">
          <button className="bg-gray-200 text-black px-4 py-3 rounded-lg flex-1 mr-2 text-center">
            ₹{totalAmount}
          </button>
          <button
            onClick={handleCreateOrder}
            disabled={!isFormValid() && !loading}
            className={`px-4 py-3 rounded-lg flex-1 ml-2 text-center font-medium transition-all duration-300 ${
              isFormValid()
                ? "bg-black text-white hover:bg-gray-800 cursor-pointer"
                : "bg-gray-400 text-gray-600 cursor-not-allowed"
            }`}
          >
            {loading?"Verifying...":"Confirm and Pay"}
          </button>
        </div>
      </div>
    </div>
  );
}

export default ReferralRequest;